/**
 * Healthcare. Doc: docs/overall-plan/02-MANGO-PERSONAL/healthcare
 * Wired to GET/POST /api/v1/healthcare.
 */
import React, { useEffect, useState } from 'react';
import { healthcareApi, type HealthcarePrescription, type HealthcareRecord } from '../services/api';

const Healthcare: React.FC = () => {
  const [prescriptions, setPrescriptions] = useState<HealthcarePrescription[]>([]);
  const [records, setRecords] = useState<HealthcareRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [tab, setTab] = useState<'prescriptions' | 'records'>('prescriptions');
  const [showAdd, setShowAdd] = useState(false);
  const [medName, setMedName] = useState('');
  const [recTitle, setRecTitle] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const loadData = React.useCallback(async (isRefresh = false) => {
    if (isRefresh) setRefreshing(true);
    else setLoading(true);
    setError(null);
    try {
      const [presRes, recRes] = await Promise.all([healthcareApi.getPrescriptions(), healthcareApi.getRecords()]);
      if (presRes?.success) setPrescriptions(Array.isArray(presRes?.data) ? presRes.data : []);
      if (recRes?.success) setRecords(Array.isArray(recRes?.data) ? recRes.data : []);
      if (!presRes?.success) setError(presRes?.error || 'Failed to load');
      if (!recRes?.success) setError(recRes?.error || 'Failed to load');
    } catch {
      setError('Failed to load');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleAddPrescription = async () => {
    if (!medName.trim()) return;
    setSubmitting(true);
    setError(null);
    try {
      const res = await healthcareApi.addPrescription({ medicationName: medName.trim() });
      if (res?.success) {
        setMedName('');
        setShowAdd(false);
        loadData(true);
      } else setError(res?.error || 'Failed to add');
    } catch {
      setError('Failed to add');
    } finally {
      setSubmitting(false);
    }
  };

  const handleAddRecord = async () => {
    if (!recTitle.trim()) return;
    setSubmitting(true);
    setError(null);
    try {
      const res = await healthcareApi.addRecord({ title: recTitle.trim() });
      if (res?.success) {
        setRecTitle('');
        setShowAdd(false);
        loadData(true);
      } else setError(res?.error || 'Failed to add');
    } catch {
      setError('Failed to add');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return <div style={{ maxWidth: '560px', margin: '0 auto', padding: '24px' }}><p>Loading…</p></div>;

  return (
    <div style={{ maxWidth: '560px', margin: '0 auto', padding: '24px' }}>
      <div style={{ background: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)', borderRadius: '16px', padding: '24px', marginBottom: '24px', color: 'white' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '12px', flexWrap: 'wrap' }}>
          <div>
            <h1 style={{ margin: '0 0 8px 0', fontSize: '24px' }}>🏥 Healthcare</h1>
            <p style={{ margin: 0, opacity: 0.95, fontSize: '14px' }}>Prescriptions and health records</p>
          </div>
          <div style={{ display: 'flex', gap: '8px' }}>
            <button type="button" onClick={() => setShowAdd((s) => !s)} style={{ padding: '8px 16px', background: 'rgba(255,255,255,0.9)', border: 'none', borderRadius: '8px', color: '#11998e', cursor: 'pointer', fontSize: '14px' }}>Add {tab === 'prescriptions' ? 'Prescription' : 'Record'}</button>
            <button type="button" onClick={() => loadData(true)} disabled={refreshing} style={{ padding: '8px 16px', background: 'rgba(255,255,255,0.2)', border: '1px solid rgba(255,255,255,0.4)', borderRadius: '8px', color: 'white', cursor: refreshing ? 'not-allowed' : 'pointer', fontSize: '14px' }}>{refreshing ? 'Refreshing…' : 'Refresh'}</button>
          </div>
        </div>
      </div>
      <div style={{ display: 'flex', gap: '8px', marginBottom: '16px' }}>
        <button type="button" onClick={() => setTab('prescriptions')} style={{ padding: '8px 16px', background: tab === 'prescriptions' ? '#11998e' : '#e2e8f0', color: tab === 'prescriptions' ? 'white' : '#334155', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '14px' }}>Prescriptions</button>
        <button type="button" onClick={() => setTab('records')} style={{ padding: '8px 16px', background: tab === 'records' ? '#11998e' : '#e2e8f0', color: tab === 'records' ? 'white' : '#334155', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '14px' }}>Records</button>
      </div>
      {showAdd && (
        <div style={{ background: '#f8fafc', borderRadius: '12px', padding: '16px', marginBottom: '16px', border: '1px solid #e2e8f0' }}>
          {tab === 'prescriptions' ? (
            <>
              <input type="text" placeholder="Medication name" value={medName} onChange={(e) => setMedName(e.target.value)} style={{ width: '100%', padding: '10px 12px', marginBottom: '10px', border: '1px solid #cbd5e1', borderRadius: '8px', fontSize: '14px' }} />
              <div style={{ display: 'flex', gap: '8px' }}>
                <button type="button" onClick={handleAddPrescription} disabled={submitting || !medName.trim()} style={{ padding: '8px 16px', background: '#16a34a', border: 'none', borderRadius: '8px', color: 'white', cursor: submitting ? 'not-allowed' : 'pointer', fontSize: '14px' }}>{submitting ? 'Adding…' : 'Add'}</button>
                <button type="button" onClick={() => { setShowAdd(false); setMedName(''); }} style={{ padding: '8px 16px', background: '#64748b', border: 'none', borderRadius: '8px', color: 'white', cursor: 'pointer', fontSize: '14px' }}>Cancel</button>
              </div>
            </>
          ) : (
            <>
              <input type="text" placeholder="Record title" value={recTitle} onChange={(e) => setRecTitle(e.target.value)} style={{ width: '100%', padding: '10px 12px', marginBottom: '10px', border: '1px solid #cbd5e1', borderRadius: '8px', fontSize: '14px' }} />
              <div style={{ display: 'flex', gap: '8px' }}>
                <button type="button" onClick={handleAddRecord} disabled={submitting || !recTitle.trim()} style={{ padding: '8px 16px', background: '#16a34a', border: 'none', borderRadius: '8px', color: 'white', cursor: submitting ? 'not-allowed' : 'pointer', fontSize: '14px' }}>{submitting ? 'Adding…' : 'Add'}</button>
                <button type="button" onClick={() => { setShowAdd(false); setRecTitle(''); }} style={{ padding: '8px 16px', background: '#64748b', border: 'none', borderRadius: '8px', color: 'white', cursor: 'pointer', fontSize: '14px' }}>Cancel</button>
              </div>
            </>
          )}
        </div>
      )}
      {error && <p style={{ color: '#b91c1c', marginBottom: '16px' }}>{error}</p>}
      {tab === 'prescriptions' && (
        <>
          {prescriptions.length === 0 && !showAdd && <div style={{ background: '#f8fafc', borderRadius: '12px', padding: '32px', textAlign: 'center', color: '#64748b', fontSize: '14px' }}>No prescriptions. Add one above.</div>}
          {prescriptions.length > 0 && (
            <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
              {prescriptions.map((p) => (
                <li key={p.id} style={{ padding: '12px 16px', borderBottom: '1px solid #e2e8f0' }}><strong>{p.medicationName}</strong>{p.dosage && <span style={{ marginLeft: '8px', color: '#64748b' }}>{p.dosage}</span>}</li>
              ))}
            </ul>
          )}
        </>
      )}
      {tab === 'records' && (
        <>
          {records.length === 0 && !showAdd && <div style={{ background: '#f8fafc', borderRadius: '12px', padding: '32px', textAlign: 'center', color: '#64748b', fontSize: '14px' }}>No records. Add one above.</div>}
          {records.length > 0 && (
            <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
              {records.map((r) => (
                <li key={r.id} style={{ padding: '12px 16px', borderBottom: '1px solid #e2e8f0' }}><strong>{r.title}</strong>{r.recordType && <span style={{ marginLeft: '8px', fontSize: '12px', color: '#64748b' }}>({r.recordType})</span>}</li>
              ))}
            </ul>
          )}
        </>
      )}
    </div>
  );
};

export default Healthcare;
